import {
  TreeView,
  TreeViewBranchControlProps,
  TreeViewItemProps,
  createTreeCollection,
  useTreeView,
  type TreeViewRootProps,
} from '@chakra-ui/react'
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { startCase } from 'lodash-es'
import { ReactNode, forwardRef, useState } from 'react'
import { useTheme } from 'utils'
import { Box, ChevronRightIcon } from '.'

export interface Props extends Omit<TreeViewRootProps, 'collection'> {
  data: TreeViewNode[]
  onRearrange?: (nodeValue: string, parentValue: string, childIndex: number) => void
}

export type TreeViewNode = TreeViewBranch | TreeViewItem

interface TreeViewBranch
  extends Omit<TreeViewBranchControlProps, 'children' | 'id'>,
    CommonTreeViewNodeProps {
  children: TreeViewNode[]
}

interface TreeViewItem
  extends Omit<TreeViewItemProps, 'children' | 'id'>,
    CommonTreeViewNodeProps {}

interface CommonTreeViewNodeProps {
  disableDrag?: boolean
  label?: ReactNode
  value: string
}

export default forwardRef<HTMLDivElement, Props>(({ data, onRearrange, ...rest }, ref) => {
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    const newLocation = findNodeLocation(data, over.id as string)
    if (!newLocation) return

    const { index, parent, node } = newLocation
    if (node.disableDrag) return

    onRearrange?.(active.id as string, parent.value, index)
  }

  const store = useTreeView({
    collection: createTreeCollection<TreeViewBranch>({
      rootNode: { value: '__ROOT', children: data },
    }),
    expandOnClick: false,
    selectionMode: 'single',
    ...rest,
  })

  // const activeNode = activeId ? data.find(node => node.value === activeId) : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        disabled={!onRearrange}
        items={data.map(item => item.value)}
        strategy={verticalListSortingStrategy}
      >
        <TreeView.RootProvider animateContent value={store}>
          <TreeView.Tree
            ref={ref}
            // css={{
            //   '--tree-indentation': '1px',
            //   '--tree-padding-inline': '1px',
            //   '--tree-icon-offset': 0,
            // }}
          >
            <TreeView.Node
              // indentGuide={<TreeView.BranchIndentGuide />}
              branchProps={{ borderRadius: 'md' }}
              branchContentProps={{ ml: 3 }}
              render={({ node, nodeState }) => (
                <SortableNode
                  data={data}
                  item={node}
                  nodeState={nodeState}
                  disableDrag={!onRearrange}
                />
              )}
            />
          </TreeView.Tree>
        </TreeView.RootProvider>
      </SortableContext>
      {/* <DragOverlay>
        {activeNode && (
          <div style={{ opacity: 0.8 }}>{activeNode.label || startCase(activeNode.value)}</div>
        )}
      </DragOverlay> */}
    </DndContext>
  )
})

interface SortableNodeProps {
  data: TreeViewNode[]
  item: TreeViewNode
  nodeState: any
  disableDrag?: boolean
}

const SortableNode = ({ data, item, nodeState, disableDrag: _disabledDrag }: SortableNodeProps) => {
  const { theme } = useTheme()
  const { disableDrag, label, value, ...rest } = item
  const disabled = disableDrag || _disabledDrag
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
    over,
  } = useSortable({
    id: item.value,
    disabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const _label = label || startCase(value)
  const activeLocation = findNodeLocation(data, active?.id as string)
  const overLocation = findNodeLocation(data, over?.id as string)
  const isDraggingOverSelf = active?.id === over?.id
  const isDraggingDownInSameParent =
    activeLocation &&
    overLocation &&
    activeLocation.parent === overLocation.parent &&
    activeLocation.index < overLocation.index

  return (
    <>
      {isOver && !isDraggingOverSelf && !isDraggingDownInSameParent && (
        <Box height="2px" backgroundColor="cyan.500" />
      )}

      {nodeState.isBranch ? (
        <TreeView.BranchControl
          key={value}
          ref={setNodeRef}
          cursor="pointer"
          // width="fit-content !important"
          px={2}
          py={1}
          borderRadius="md"
          _hover={{
            bg: theme === 'light' ? 'gray.100' : 'gray.700',
          }}
          _selected={{
            bg: theme === 'light' ? 'blue.100' : 'blue.900',
            color: theme === 'light' ? 'blue.800' : 'blue.200',
          }}
          id={value}
          style={style}
          {...attributes}
          {...rest}
          {...(!disabled ? listeners : {})}
        >
          <TreeView.BranchTrigger>
            <TreeView.BranchIndicator asChild>
              <ChevronRightIcon size="sm" />
            </TreeView.BranchIndicator>
          </TreeView.BranchTrigger>
          <TreeView.BranchText textStyle="bodyMedium" whiteSpace="nowrap">
            {_label}
          </TreeView.BranchText>
        </TreeView.BranchControl>
      ) : (
        <TreeView.Item
          key={value}
          ref={setNodeRef}
          style={style}
          {...attributes}
          cursor="pointer"
          px={2}
          py={1}
          borderRadius="md"
          ml={6}
          _hover={{
            bg: theme === 'light' ? 'gray.100' : 'gray.700',
          }}
          _selected={{
            bg: theme === 'light' ? 'blue.100' : 'blue.900',
            color: theme === 'light' ? 'blue.800' : 'blue.200',
          }}
          id={value}
          {...rest}
          {...(!disabled ? listeners : {})}
        >
          <TreeView.ItemText textStyle="bodyMedium" whiteSpace="nowrap">
            {_label}
          </TreeView.ItemText>
        </TreeView.Item>
      )}

      {isOver && !isDraggingOverSelf && isDraggingDownInSameParent && (
        <Box height="2px" backgroundColor="cyan.500" />
      )}
    </>
  )
}

interface NodeLocation {
  index: number
  node: TreeViewNode
  parent: TreeViewBranch
}

function findNodeLocation(nodes: TreeViewNode[], childId: string): NodeLocation | undefined {
  for (const node of nodes) {
    if ('children' in node) {
      const index = node.children.findIndex(child => child.value === childId)
      if (index > -1) return { parent: node, index, node: node.children[index] }

      const found = findNodeLocation(node.children, childId)
      if (found) return found
    }
  }
}
